import { ref } from 'vue'

/**
 * Controller for the SINGLE shared user hover-card instance (mounted once in
 * ChatPanel, teleported to body). Online-list rows / strip avatars drive it:
 *
 *   @mouseenter → show(userId, el.getBoundingClientRect())
 *   @mouseleave → scheduleHide()
 *
 * and the card itself calls cancelHide() on enter / scheduleHide() on leave so
 * moving the cursor from a row INTO the card doesn't dismiss it.
 *
 * Open/close are debounced: a small show-delay avoids spamming card fetches
 * while the cursor sweeps across rows; a hide-delay bridges the row→card gap.
 */

const SHOW_DELAY_MS = 130
const HIDE_DELAY_MS = 180

const activeUserId = ref<string | null>(null)
const anchorRect = ref<DOMRect | null>(null)
const visible = ref(false)
/** true when the card was opened by a click/tap (pinned) rather than hover —
 *  pinned cards ignore scheduleHide so touch users can interact with actions. */
const pinned = ref(false)

let showTimer: ReturnType<typeof setTimeout> | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null

function clearTimers() {
    if (showTimer) { clearTimeout(showTimer); showTimer = null }
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null }
}

function show(userId: string, rect: DOMRect) {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null }
    if (showTimer) clearTimeout(showTimer)
    // If already showing this user, just refresh the anchor (e.g. on scroll).
    if (visible.value && activeUserId.value === userId) {
        anchorRect.value = rect
        return
    }
    showTimer = setTimeout(() => {
        activeUserId.value = userId
        anchorRect.value = rect
        visible.value = true
        pinned.value = false
        showTimer = null
    }, SHOW_DELAY_MS)
}

/** Click/tap open — stays until explicitly dismissed (touch-friendly). */
function pin(userId: string, rect: DOMRect) {
    clearTimers()
    activeUserId.value = userId
    anchorRect.value = rect
    visible.value = true
    pinned.value = true
}

function scheduleHide() {
    if (pinned.value) return
    if (showTimer) { clearTimeout(showTimer); showTimer = null }
    if (hideTimer) clearTimeout(hideTimer)
    hideTimer = setTimeout(() => {
        visible.value = false
        hideTimer = null
    }, HIDE_DELAY_MS)
}

function cancelHide() {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null }
}

function hideNow() {
    clearTimers()
    visible.value = false
    pinned.value = false
}

export function useHoverCard() {
    return { activeUserId, anchorRect, visible, pinned, show, pin, scheduleHide, cancelHide, hideNow }
}
